import { create } from 'zustand';

/**
 * Auth state.
 *
 * Storage choice: ALL fields go into localStorage so that opening a viewer in
 * a new tab inherits the session — sessionStorage is per-tab and would force
 * a re-login in every popout. "Remember me" no longer affects token durability
 * (token always persists until logout); it only controls whether the username
 * is pre-filled on next login.
 */
function getStored(key: string): string | null {
  return localStorage.getItem(key);
}

interface AuthState {
  acctoken: string | null;
  displayName: string | null;
  userId: number | null;
  /** Login username of the currently signed-in user. Persisted regardless
   * of the rememberMe flag so feature-gating (e.g. hide the Composer
   * buttons for anyone other than "sriram") can reliably identify the
   * user without waiting for a re-login. Separate from rememberedUsername
   * which only tracks the "pre-fill on next login" preference. */
  username: string | null;
  /** Last-used username, kept across logout when rememberMe was checked. */
  rememberedUsername: string | null;
  login: (acctoken: string, displayName: string, userId: number, rememberMe: boolean, username?: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  acctoken: getStored('acctoken'),
  displayName: getStored('displayName'),
  userId: Number(getStored('userId')) || null,
  username: getStored('username'),
  rememberedUsername: getStored('rememberedUsername'),
  login: (acctoken, displayName, userId, rememberMe, username) => {
    localStorage.setItem('acctoken', acctoken);
    localStorage.setItem('displayName', displayName);
    localStorage.setItem('userId', String(userId));
    if (username) localStorage.setItem('username', username);
    else localStorage.removeItem('username');
    if (rememberMe && username) {
      localStorage.setItem('rememberedUsername', username);
    } else if (!rememberMe) {
      localStorage.removeItem('rememberedUsername');
    }
    // Migration: clear any leftovers from the old sessionStorage path
    sessionStorage.removeItem('acctoken');
    sessionStorage.removeItem('displayName');
    sessionStorage.removeItem('userId');
    set({
      acctoken,
      displayName,
      userId,
      username: username ?? null,
      rememberedUsername: rememberMe && username ? username : null,
    });
  },
  logout: () => {
    // Preserve rememberedUsername across logout so the next login pre-fills.
    const remembered = localStorage.getItem('rememberedUsername');
    localStorage.removeItem('acctoken');
    localStorage.removeItem('displayName');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    sessionStorage.removeItem('acctoken');
    sessionStorage.removeItem('displayName');
    sessionStorage.removeItem('userId');
    set({
      acctoken: null,
      displayName: null,
      userId: null,
      username: null,
      rememberedUsername: remembered,
    });
  },
}));
